/**
 * Client for the rgb-lib-server HTTP API (AluVM-based RGB wallet).
 *
 * The API requires a token on every route but health, and a wallet's password
 * on every route that addresses it. The client never holds the token: in the
 * desktop app the main process makes the call, and on the web a same-origin
 * proxy adds it.
 */

import { getElectronAPI } from "../adapters/electron";
import { RGB_LIB_API_CONFIG } from "../config";
import {
  type ApiReply,
  type ApiRequest,
  type Send,
  fetchSender,
  replyErrorMessage,
  transportError,
} from "./apiTransport";

/**
 * A failed API call. `code` is `CONNECTION_ERROR`, `TIMEOUT`, `UNKNOWN_ERROR`,
 * or `HTTP_<status>` when the server answered with an error.
 */
export class RgbLibApiException extends Error {
  constructor(
    public code: string,
    message: string,
    /** HTTP status, when the server answered */
    public status?: number,
  ) {
    super(message);
    this.name = "RgbLibApiException";
  }
}

// ============================================================
// Response Types (matching rgb-lib native output)
// ============================================================

export interface RgbLibNetworkResponse {
  network: string;
  indexer_url: string;
  proxy_url: string;
}

export interface RgbLibRestoreWalletResponse {
  wallet_id: string;
  xpub: string;
  account_xpub_colored: string;
  account_xpub_vanilla: string;
  network: string;
}

export interface RgbLibAddressResponse {
  address: string;
}

export interface RgbLibBtcBalance {
  vanilla: RgbLibBtcBalanceDetail;
  colored: RgbLibBtcBalanceDetail;
}

export interface RgbLibBtcBalanceDetail {
  settled: number;
  future: number;
  spendable: number;
}

export interface RgbLibCreateUtxosResponse {
  created: number;
}

export interface RgbLibAssetNIA {
  asset_id: string;
  ticker: string;
  name: string;
  precision: number;
  balance: RgbLibBalance;
  issued_supply: number;
  added_at: number;
  media?: RgbLibMedia | null;
}

export interface RgbLibBalance {
  settled: number;
  future: number;
  spendable: number;
}

export interface RgbLibMedia {
  file_path: string;
  mime: string;
}

export interface RgbLibAssets {
  nia?: RgbLibAssetNIA[];
  uda?: unknown[];
  cfa?: unknown[];
  ifa?: unknown[];
}

export interface RgbLibReceiveData {
  recipient_id: string;
  invoice: string;
  expiration_timestamp?: number | null;
  batch_transfer_idx: number;
}

export interface RgbLibOperationResult {
  txid: string;
  batch_transfer_idx: number;
}

export type RgbLibTransferStatus =
  | "WaitingCounterparty"
  | "WaitingConfirmations"
  | "Settled"
  | "Failed";

export type RgbLibTransferKind =
  | "Issuance"
  | "ReceiveBlind"
  | "ReceiveWitness"
  | "Send"
  | "Inflation";

/** rgb-lib's `Assignment`: externally tagged, so `{ Fungible: 100 }` or `"NonFungible"`. */
export type RgbLibAssignment =
  | { Fungible: number }
  | { InflationRight: number }
  | "NonFungible"
  | "ReplaceRight"
  | "Any";

export interface RgbLibTransfer {
  idx: number;
  batch_transfer_idx: number;
  created_at: number;
  updated_at: number;
  status: RgbLibTransferStatus;
  requested_assignment: RgbLibAssignment | null;
  assignments: RgbLibAssignment[];
  kind: RgbLibTransferKind;
  txid: string | null;
  recipient_id: string | null;
  receive_utxo: RgbLibOutpoint | null;
  change_utxo: RgbLibOutpoint | null;
  expiration: number | null;
  transport_endpoints: RgbLibTransportEndpoint[];
  invoice_string: string | null;
  consignment_path: string | null;
}

export interface RgbLibTransportEndpoint {
  endpoint: string;
  transport_type: string;
  used: boolean;
}

export interface RgbLibUnspent {
  utxo: RgbLibOutpoint;
  rgb_allocations: RgbLibRgbAllocation[];
}

export interface RgbLibOutpoint {
  txid: string;
  vout: number;
}

export interface RgbLibRgbAllocation {
  asset_id?: string | null;
  amount: number;
  settled: boolean;
}

// ============================================================
// Request Types
// ============================================================

export interface RgbLibCreateUtxosParams {
  up_to?: boolean;
  num?: number;
  size?: number;
  fee_rate?: number;
}

export interface RgbLibIssueAssetNiaParams {
  ticker: string;
  name: string;
  precision?: number;
  amounts: number[];
}

export interface RgbLibBlindReceiveParams {
  asset_id?: string;
  min_confirmations?: number;
  transport_endpoints?: string[];
}

export interface RgbLibSendRecipient {
  recipient_id: string;
  amount: number;
  /** Defaults to the server's configured RGB proxy. */
  transport_endpoints?: string[];
}

export interface RgbLibSendParams {
  recipient_map: Record<string, RgbLibSendRecipient[]>;
  fee_rate?: number;
  min_confirmations?: number;
  donation?: boolean;
}

export interface RgbLibRefreshParams {
  asset_id?: string;
}

export interface RgbLibApiConfig {
  /** API base URL. Setting it bypasses the desktop app's main-process bridge. */
  baseUrl: string;
  timeout?: number;
  /**
   * API token, for callers that reach the server directly (tests, scripts).
   * Browser code must not set it: a token in a web bundle is public.
   */
  apiToken?: string;
}

interface RequestOptions {
  method?: ApiRequest["method"];
  password?: string;
  json?: unknown;
}

const walletPath = (walletId: string): string => `/wallets/${encodeURIComponent(walletId)}`;

export class RgbLibApiClient {
  private timeout: number;
  private dispatch: Send;

  constructor(config?: Partial<RgbLibApiConfig>) {
    const electron = config?.baseUrl ? undefined : getElectronAPI();
    if (electron) {
      this.dispatch = (request) => electron.rgbLibApiRequest(request);
    } else {
      this.dispatch = fetchSender(
        config?.baseUrl ?? RGB_LIB_API_CONFIG.WEB_API_URL,
        config?.apiToken,
      );
    }
    this.timeout = config?.timeout ?? RGB_LIB_API_CONFIG.DEFAULT_TIMEOUT;
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    let reply: ApiReply;
    try {
      reply = await this.dispatch({
        method: options.method ?? "GET",
        path,
        password: options.password,
        body: options.json === undefined ? undefined : JSON.stringify(options.json),
        timeoutMs: this.timeout,
      });
    } catch (error) {
      const { code, message } = transportError(error);
      throw new RgbLibApiException(
        code,
        code === "CONNECTION_ERROR" ? "Cannot connect to rgb-lib-server" : message,
      );
    }

    if (reply.status < 200 || reply.status >= 300) {
      throw new RgbLibApiException(`HTTP_${reply.status}`, replyErrorMessage(reply), reply.status);
    }
    try {
      return JSON.parse(reply.body) as T;
    } catch {
      throw new RgbLibApiException("UNKNOWN_ERROR", "The server sent a response that is not JSON");
    }
  }

  /** The indexer and RGB proxy the server uses for wallets on `network`. */
  async getNetwork(network: string): Promise<RgbLibNetworkResponse> {
    return this.request(`/network?network=${encodeURIComponent(network)}`);
  }

  // ============================================================
  // Wallets
  // ============================================================

  /**
   * Restore the wallet a phrase belongs to. Restoring a wallet the server already
   * holds succeeds with its password and returns the same id.
   */
  async restoreWallet(
    network: string,
    mnemonic: string,
    password: string,
  ): Promise<RgbLibRestoreWalletResponse> {
    return this.request("/wallets/restore", {
      method: "POST",
      json: { network, mnemonic, password },
    });
  }

  async deleteWallet(walletId: string, password: string): Promise<{ deleted: boolean }> {
    return this.request(walletPath(walletId), { method: "DELETE", password });
  }

  // ============================================================
  // Bitcoin Operations
  // ============================================================

  async getAddress(walletId: string, password: string): Promise<RgbLibAddressResponse> {
    return this.request(`${walletPath(walletId)}/address`, { password });
  }

  async getBalance(walletId: string, password: string): Promise<RgbLibBtcBalance> {
    return this.request(`${walletPath(walletId)}/balance`, { password });
  }

  async createUtxos(
    walletId: string,
    password: string,
    params?: RgbLibCreateUtxosParams,
  ): Promise<RgbLibCreateUtxosResponse> {
    return this.request(`${walletPath(walletId)}/create-utxos`, {
      method: "POST",
      password,
      json: params ?? {},
    });
  }

  // ============================================================
  // RGB Asset Operations
  // ============================================================

  async issueAssetNia(
    walletId: string,
    password: string,
    params: RgbLibIssueAssetNiaParams,
  ): Promise<RgbLibAssetNIA> {
    return this.request(`${walletPath(walletId)}/issue-asset-nia`, {
      method: "POST",
      password,
      json: params,
    });
  }

  async listAssets(walletId: string, password: string): Promise<RgbLibAssets> {
    return this.request(`${walletPath(walletId)}/assets`, { password });
  }

  // ============================================================
  // RGB Transfer Operations
  // ============================================================

  async blindReceive(
    walletId: string,
    password: string,
    params?: RgbLibBlindReceiveParams,
  ): Promise<RgbLibReceiveData> {
    return this.request(`${walletPath(walletId)}/blind-receive`, {
      method: "POST",
      password,
      json: params ?? {},
    });
  }

  async send(
    walletId: string,
    password: string,
    params: RgbLibSendParams,
  ): Promise<RgbLibOperationResult> {
    return this.request(`${walletPath(walletId)}/send`, {
      method: "POST",
      password,
      json: params,
    });
  }

  /** Send bitcoin from the wallet's vanilla outputs; `amount` in satoshis. */
  async sendBtc(
    walletId: string,
    password: string,
    params: { address: string; amount: number; fee_rate?: number },
  ): Promise<{ txid: string }> {
    return this.request(`${walletPath(walletId)}/send-btc`, {
      method: "POST",
      password,
      json: params,
    });
  }

  async refresh(
    walletId: string,
    password: string,
    params?: RgbLibRefreshParams,
  ): Promise<{ transfers_changed: boolean }> {
    return this.request(`${walletPath(walletId)}/refresh`, {
      method: "POST",
      password,
      json: params ?? {},
    });
  }

  async listTransfers(
    walletId: string,
    password: string,
    assetId?: string,
  ): Promise<RgbLibTransfer[]> {
    const query = assetId ? `?asset_id=${encodeURIComponent(assetId)}` : "";
    return this.request(`${walletPath(walletId)}/transfers${query}`, { password });
  }

  async listUnspents(
    walletId: string,
    password: string,
    settledOnly?: boolean,
  ): Promise<RgbLibUnspent[]> {
    const query = settledOnly ? "?settled_only=true" : "";
    return this.request(`${walletPath(walletId)}/unspents${query}`, { password });
  }
}
